import React, { useCallback, useContext, useEffect, useState } from "react";
import moment from "moment";
import Swal from "sweetalert2";
import MenuButton from "../MenuButton/MenuButton";
import { NoteState } from "../../context/notes/NoteState";
import UserServices from "../../UserServices/UserServices";
import "./LockerBoard.css";

function heldLabel(from, until) {
  if (!from) return "";
  const start = new Date(String(from).replace(" ", "T")).getTime();
  const end = typeof until === "number"
    ? until
    : until
      ? new Date(String(until).replace(" ", "T")).getTime()
      : Date.now();
  if (!Number.isFinite(start) || !Number.isFinite(end)) return "";
  let mins = Math.max(0, Math.floor((end - start) / 60000));
  const weeks = Math.floor(mins / (60 * 24 * 7));
  mins -= weeks * 60 * 24 * 7;
  const days = Math.floor(mins / (60 * 24));
  mins -= days * 60 * 24;
  const hours = Math.floor(mins / 60);
  mins -= hours * 60;
  if (weeks > 0) return `${weeks} wk ${days} day${days === 1 ? "" : "s"}`;
  if (days > 0) return `${days} day${days === 1 ? "" : "s"} ${hours} hr`;
  if (hours > 0) return `${hours} hr ${mins} min`;
  return `${mins} min`;
}

function downloadCsv(filename, rows) {
  const headers = ["Type", "Side", "Locker", "Client", "Membership no", "Started", "Ended", "Held for", "Status", "Note"];
  const lines = [headers.join(",")];
  rows.forEach((row) => {
    const cells = [
      row.record_type === "damage" ? "Out of service" : "Key",
      row.gender === "female" ? "Female" : "Male",
      row.locker_number,
      row.client_name,
      row.client_uid,
      row.started_at,
      row.ended_at || "",
      row.record_type === "damage" ? "" : heldLabel(row.started_at, row.ended_at || undefined),
      row.status,
      row.note,
    ].map((value) => `"${String(value ?? "").replace(/"/g, '""')}"`);
    lines.push(cells.join(","));
  });
  const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

export default function LockerBoard() {
  const { state } = useContext(NoteState);
  const savedBranch = Number(state?.branch_id);
  const [branchId, setBranchId] = useState(savedBranch === 1 || savedBranch === 15 ? savedBranch : 15);
  const [gender, setGender] = useState("male");

  const [board, setBoard] = useState(null);
  const [fromNo, setFromNo] = useState("54");
  const [toNo, setToNo] = useState("150");
  const [showSetup, setShowSetup] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const [look, setLook] = useState("all");
  const [selected, setSelected] = useState(null);
  const [query, setQuery] = useState("");
  const [matches, setMatches] = useState([]);
  const [client, setClient] = useState(null);
  const [busy, setBusy] = useState(false);
  const [reportFrom, setReportFrom] = useState(moment().startOf("month").format("YYYY-MM-DD"));
  const [reportTo, setReportTo] = useState(moment().format("YYYY-MM-DD"));
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(timer);
  }, []);

  const load = useCallback(async () => {
    if (!branchId) return;
    try {
      let res = await UserServices.lockerBoard(branchId, gender);
      let data = res.data?.data || null;
      if (gender === "male" && !data?.lockers?.length) {
        res = await UserServices.lockerSyncRange({
          branch_id: branchId,
          gender: "male",
          from: 54,
          to: 150,
        });
        data = res.data?.data || null;
      }
      setBoard(data);
      setFromNo(data?.range?.from ? String(data.range.from) : "");
      setToNo(data?.range?.to ? String(data.range.to) : "");
    } catch (err) {
      if (err?.response) {
        Swal.fire("Could not load lockers", err.response?.data?.message || "Try again.", "error");
      }
    }
  }, [branchId, gender]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!selected || selected.status !== "available" || query.trim().length < 2) {
      setMatches([]);
      return undefined;
    }
    const timer = setTimeout(async () => {
      try {
        const res = await UserServices.lockerSearchClients(branchId, query.trim(), gender);
        setMatches(res.data?.data || []);
      } catch {
        setMatches([]);
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [query, selected, branchId, gender]);

  const lockers = board?.lockers || [];

  const applyRange = async () => {
    setBusy(true);
    try {
      const res = await UserServices.lockerSyncRange({
        branch_id: branchId,
        gender,
        from: Number(fromNo),
        to: Number(toNo),
      });
      const data = res.data?.data;
      setBoard({ range: data.range, counts: data.counts, lockers: data.lockers });
      setShowSetup(false);
    } catch (err) {
      Swal.fire("Numbers not saved", err?.response?.data?.message || "Check the numbers.", "error");
    } finally {
      setBusy(false);
    }
  };

  const closeSheet = () => {
    setSelected(null);
    setQuery("");
    setMatches([]);
    setClient(null);
  };

  const runAction = async (action) => {
    if (!selected) return;
    setBusy(true);
    try {
      const payload = { branch_id: branchId, client_id: client?.id };
      if (action === "assign") await UserServices.lockerAssign(selected.id, payload);
      if (action === "return") await UserServices.lockerReturnKey(selected.id, payload);
      if (action === "damage") await UserServices.lockerMarkDamaged(selected.id, payload);
      if (action === "repair") await UserServices.lockerMarkRepaired(selected.id, payload);
      closeSheet();
      await load();
    } catch (err) {
      Swal.fire("Not saved", err?.response?.data?.message || "Try again.", "error");
    } finally {
      setBusy(false);
    }
  };

  const downloadReport = async () => {
    setBusy(true);
    try {
      const res = await UserServices.lockerReport({
        branch_id: branchId,
        from: reportFrom,
        to: reportTo,
      });
      const rows = res.data?.data?.rows || [];
      if (!rows.length) {
        Swal.fire("No history", "Nothing was recorded in these dates.", "info");
        return;
      }
      downloadCsv(`locker-report-${reportFrom}-to-${reportTo}.csv`, rows);
    } catch (err) {
      Swal.fire("Report failed", err?.response?.data?.message || "Try again.", "error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="wrapper locker-page">
      <main className="page-content customer-contnent locker-fill">
        <div className="site-main-design">
          <MenuButton hideSearch />
          <div className="card locker-stage">
            <div className="card-header py-2 d-flex justify-content-between align-items-center flex-wrap gap-2">
              <h5 className="mb-0 font-20">Lockers</h5>
              <div className="d-flex align-items-center gap-2 flex-wrap">
                <select
                  className="form-select form-select-sm"
                  style={{ width: 140 }}
                  value={String(branchId)}
                  onChange={(e) => {
                    setBranchId(Number(e.target.value));
                    setSelected(null);
                    setShowSetup(false);
                  }}
                >
                  <option value={15}>F-11</option>
                  <option value={1}>G-13</option>
                </select>
                <select
                  className="form-select form-select-sm"
                  style={{ width: 130 }}
                  value={gender}
                  onChange={(e) => {
                    setGender(e.target.value);
                    setSelected(null);
                    setShowSetup(false);
                  }}
                >
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                </select>
                <div className="locker-radios">
                  {[
                    ["all", "All"],
                    ["available", "Free"],
                    ["occupied", "Reserved"],
                    ["damaged", "Damaged"],
                  ].map(([key, label]) => (
                    <label key={key} className={look === key ? "on" : ""}>
                      <input type="radio" name="locker-status" checked={look === key} onChange={() => setLook(key)} />
                      {label}
                    </label>
                  ))}
                </div>
                <button type="button" className="btn btn-outline-danger btn-sm" onClick={() => { setShowSetup((v) => !v); setShowReport(false); }}>
                  Numbers
                </button>
                <button type="button" className="btn btn-outline-danger btn-sm" onClick={() => { setShowReport((v) => !v); setShowSetup(false); }}>
                  Report
                </button>
              </div>
            </div>
            <div className="card-body locker-body">
          {(showSetup || lockers.length === 0) && (
            <div className="locker-extra">
              <p className="small text-muted mb-2">{gender === "female" ? "Female" : "Male"} locker numbers. The other side keeps its own numbers.</p>
              <div className="row g-2 align-items-end">
                <div className="col-4">
                  <label className="form-label small mb-1">First locker</label>
                  <input className="form-control" value={fromNo} onChange={(e) => setFromNo(e.target.value)} />
                </div>
                <div className="col-4">
                  <label className="form-label small mb-1">Last locker</label>
                  <input className="form-control" value={toNo} onChange={(e) => setToNo(e.target.value)} />
                </div>
                <div className="col-4">
                  <button className="btn btn-dark w-100" disabled={busy} onClick={applyRange}>Show these lockers</button>
                </div>
              </div>
            </div>
          )}

          {showReport && (
            <div className="locker-extra">
              <div className="row g-2 align-items-end">
                <div className="col-4">
                  <label className="form-label small mb-1">From</label>
                  <input type="date" className="form-control" value={reportFrom} onChange={(e) => setReportFrom(e.target.value)} />
                </div>
                <div className="col-4">
                  <label className="form-label small mb-1">To</label>
                  <input type="date" className="form-control" value={reportTo} onChange={(e) => setReportTo(e.target.value)} />
                </div>
                <div className="col-4">
                  <button className="btn btn-dark w-100" disabled={busy} onClick={downloadReport}>Download</button>
                </div>
              </div>
            </div>
          )}

          {lockers.length === 0 ? (
            <div className="locker-extra">Set the first and last {gender === "female" ? "female" : "male"} locker, then press Show these lockers.</div>
          ) : (
            <div className="locker-wall">
              <div className="locker-grid">
                {lockers.map((locker) => {
                  const dim = look !== "all" && locker.status !== look;
                  return (
                    <button
                      key={locker.id}
                      type="button"
                      className={`locker-door ${locker.status} ${dim ? "dim" : ""}`}
                      onClick={() => {
                        setSelected(locker);
                        setClient(null);
                        setQuery("");
                      }}
                    >
                      <span className="plate">{locker.number}</span>
                      <span className="who">
                        {locker.status === "occupied" ? locker.client_name : locker.status === "damaged" ? "Broken" : "Free"}
                      </span>
                      {locker.status === "occupied" && locker.key_given_at ? (
                        <span className="held">{heldLabel(locker.key_given_at, now)}</span>
                      ) : null}
                      <span className="vent" />
                      <span className="keyhole" />
                    </button>
                  );
                })}
              </div>
            </div>
          )}
            </div>
          </div>
        </div>
      </main>

      {selected && (
        <div className="locker-sheet" onClick={closeSheet}>
          <div className="locker-card" onClick={(e) => e.stopPropagation()}>
            <h3>{selected.gender === "female" ? "Female" : "Male"} locker {selected.number}</h3>
            <p className="text-muted mb-2">
              {selected.status === "occupied"
                ? `${selected.client_name || "Client"}${selected.client_uid ? " · " + selected.client_uid : ""}`
                : selected.status === "damaged"
                  ? "This door is broken. It cannot be given out."
                  : "This door is free."}
            </p>
            {selected.key_given_at && selected.status === "occupied" && (
              <p className="small mb-2">Key held for {heldLabel(selected.key_given_at, now)}</p>
            )}

            {selected.status === "available" && (
              <>
                <input
                  className="form-control"
                  placeholder={gender === "female" ? "Type a female client from this branch" : "Type a male client from this branch"}
                  value={query}
                  autoFocus
                  onChange={(e) => { setQuery(e.target.value); setClient(null); }}
                />
                {client && <div className="alert alert-success py-2 mt-2 mb-0">{client.name}</div>}
                {!client && matches.map((row) => (
                  <button key={row.id} type="button" className="client-hit" onClick={() => setClient(row)}>
                    {row.name}{row.uid ? <><br /><small className="text-muted">{row.uid}</small></> : null}
                  </button>
                ))}
                <button className="big-btn give" disabled={busy || !client} onClick={() => runAction("assign")}>Give key</button>
                <button className="big-btn break" disabled={busy} onClick={() => runAction("damage")}>Mark broken</button>
              </>
            )}

            {selected.status === "occupied" && (
              <>
                <button className="big-btn back" disabled={busy} onClick={() => runAction("return")}>Key is back</button>
                <button className="big-btn break" disabled={busy} onClick={() => runAction("damage")}>Key back, locker broken</button>
              </>
            )}

            {selected.status === "damaged" && (
              <button className="big-btn fix" disabled={busy} onClick={() => runAction("repair")}>Fixed — make it free</button>
            )}

            <button type="button" className="btn btn-link w-100 mt-1" onClick={closeSheet}>Close</button>
          </div>
        </div>
      )}
    </div>
  );
}
